// src/OmeloWidget.tsx
import React, { forwardRef, useState, useEffect, useImperativeHandle, useRef } from 'react'
import './OmeloWidget.css'
import { ChevronDown } from 'lucide-react'
import { SendHorizontal } from 'lucide-react'
import omeloLogo from './assets/image-1.webp'
type Message = {
  sender: 'user' | 'bot'
  text: string
  timestamp: Date
  messageType?: 'text' | 'diagnostic' | 'products' | 'emergency'
  data?: any
}

type ChatResponse = {
  session_id: string
  query: string
  response?: string
  json_response?: any
  query_type?: string
  language?: string
  products_found?: number
  timestamp: string
}

type DiagnosticQuestion = {
  text: string
  options: Array<{
    id: string
    text: string
    value: string
    copy_text: string
  }>
}

type ProductRecommendation = {
  title: string
  brand: string
  price: string
  description: string
  features: string[]
  benefits: string[]
  url: string
}

interface OmeloWidgetProps {
  apiBaseUrl?: string
  partnerId?: string
  theme?: 'light' | 'dark'
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
  language?: 'en' | 'hi' | 'hinglish'
  enableDiagnosticFlow?: boolean
  enableProductSearch?: boolean
  enableEmergencyDetection?: boolean
  maxQuestions?: number
}

const OmeloWidget = forwardRef<any, OmeloWidgetProps>(({ 
  apiBaseUrl = 'http://localhost:8000',
  partnerId = 'default',
  theme = 'light',
  position = 'bottom-right',
  language = 'en',
  enableDiagnosticFlow = true,
  enableProductSearch = true,
  enableEmergencyDetection = true,
  maxQuestions = 4
}: OmeloWidgetProps, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [sessionId, setSessionId] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [diagnosticStep, setDiagnosticStep] = useState(0)
  const [inDiagnosticFlow, setInDiagnosticFlow] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(true)
  const [suggestionsAnimating, setSuggestionsAnimating] = useState(false)

  // Ref for messages container
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom function
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }

  // Smart auto-scroll - only scroll if user is near bottom
  const smartScrollToBottom = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100 // Within 100px of bottom
      
      if (isNearBottom) {
        scrollToBottom()
      }
    }
  }

  // Force scroll to bottom (used for bot responses)
  const forceScrollToBottom = () => {
    // Immediate scroll
    setTimeout(() => {
      scrollToBottom()
    }, 50)
    
    // Additional scroll after a longer delay to ensure DOM is fully updated
    setTimeout(() => {
      scrollToBottom()
    }, 200)
    
    // Final scroll attempt
    setTimeout(() => {
      scrollToBottom()
    }, 500)
  }

  // Auto-scroll when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        smartScrollToBottom()
      })
    }
  }, [messages])

  // Auto-scroll when chat opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is updated
      setTimeout(scrollToBottom, 100)
    }
  }, [isOpen])

  // Auto-scroll when loading state changes (for typing indicator)
  useEffect(() => {
    if (isLoading) {
      requestAnimationFrame(() => {
        scrollToBottom()
      })
    }
  }, [isLoading])

  // Handle escape key to close chat
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey)
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen])

  // Suggestion messages
  const suggestionMessages = [
    "Best food for my pet?",
    "My pet is not eating",
    "What vaccines does my pet need?",
    "How to train my puppy?",
    "Pet grooming tips",
    "What should I feed my puppy?",
     ]

  // Initialize session and check health on mount
  useEffect(() => {
    checkHealth()
    generateSessionId()
  }, [])

  const checkHealth = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/health`)
      if (response.ok) {
        setIsConnected(true)
        console.log('✅ Omelo API connected successfully')
      }
    } catch (error) {
      console.warn('⚠️ Omelo API not accessible:', error)
      setIsConnected(false)
    }
  }

  const generateSessionId = () => {
    const newSessionId = `${partnerId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    setSessionId(newSessionId)
  }

  const handleOptionClick = (optionText: string) => {
    setInput(optionText)
    sendMessage(optionText)
  }

  const handleSuggestionClick = (suggestion: string) => {
    hideSuggestionsWithAnimation()
    sendMessage(suggestion)
  }

  const hideSuggestionsWithAnimation = () => {
    setSuggestionsAnimating(true)
    setTimeout(() => {
      setShowSuggestions(false)
      setSuggestionsAnimating(false)
    }, 300)
  }

  const showSuggestionsWithAnimation = () => {
    setShowSuggestions(true)
    setSuggestionsAnimating(false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
    
    // Auto-resize textarea
    const textarea = e.target
    textarea.style.height = '44px' // Reset to min height
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px` // Expand up to max height
    
    if (e.target.value.trim() === '') {
      showSuggestionsWithAnimation()
    } else {
      hideSuggestionsWithAnimation()
    }
  }

  const sendMessage = async (message: string) => {
    if (!message.trim() || isLoading) return;

    const userMessage = {
      sender: 'user' as const,
      text: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: message,
          session_id: sessionId,
          partner_id: partnerId,  // NEW: Send partner ID for validation
          language: language
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: ChatResponse = await response.json()
      
      // Determine message type and handle structured responses
      let messageType: 'text' | 'diagnostic' | 'products' | 'emergency' = 'text'
      let responseText = data.response || 'I apologize, but I couldn\'t process your request.'
      let messageData: any = null

      // Handle different query types
      if (data.query_type === 'symptom_assessment' && data.json_response) {
        messageType = 'diagnostic'
        messageData = data.json_response
        setInDiagnosticFlow(true)
        setDiagnosticStep(prev => prev + 1)
      } else if (data.query_type === 'assessment' && data.json_response) {
        messageType = 'emergency' // Final assessments are shown as emergency
        messageData = data.json_response
        // Set response text from the assessment data
        const assessment = data.json_response.display || {}
        responseText = `${assessment.acknowledgment || ''}\n\n**Assessment Summary:**\n${assessment.symptom_summary || ''}\n\n**Recommendation:**\n${assessment.recommendation || ''}\n\n**Next Steps:**\n${(assessment.next_steps || []).map((step: string, i: number) => `${i + 1}. ${step}`).join('\n')}\n\n${assessment.reassurance || ''}`
      } else if (data.query_type === 'product_recommendation' && data.products_found && data.products_found > 0) {
        messageType = 'products'
        // Parse product information from response text
        messageData = parseProductsFromResponse(responseText)
      } else if (enableEmergencyDetection && isEmergencyResponse(responseText)) {
        messageType = 'emergency'
      }

      // Check if we should exit diagnostic flow
      if (data.query_type === 'assessment' || diagnosticStep >= maxQuestions) {
        setInDiagnosticFlow(false)
        setDiagnosticStep(0)
      }

      const botMessage: Message = {
        sender: 'bot',
        text: responseText,
        timestamp: new Date(),
        messageType: messageType,
        data: messageData
      }
      setMessages(prev => [...prev, botMessage])
      
      // Force scroll to bottom when bot responds
      forceScrollToBottom()

    } catch (error) {
      console.error('Chat error:', error)
      const errorMessage: Message = {
        sender: 'bot',
        text: isConnected 
          ? 'Sorry, I encountered an error. Please try again in a moment.'
          : 'I\'m currently offline. Please check back later.',
        timestamp: new Date(),
        messageType: 'text'
      }
      setMessages(prev => [...prev, errorMessage])
      
      // Force scroll to bottom for error messages too
      forceScrollToBottom()
    } finally {
      setIsLoading(false)
      // Show suggestions again if input is empty and not in diagnostic flow
      if (input.trim() === '' && !inDiagnosticFlow) {
        setTimeout(() => showSuggestionsWithAnimation(), 500)
      }
    }
  }

  const parseProductsFromResponse = (responseText: string): ProductRecommendation[] => {
    const products: ProductRecommendation[] = []
    const lines = responseText.split('\n')
    let currentProduct: Partial<ProductRecommendation> = {}
    
    for (const line of lines) {
      if (line.startsWith('**') && line.includes('**') && line.includes('.')) {
        // New product found
        if (currentProduct.title) {
          products.push(currentProduct as ProductRecommendation)
        }
        currentProduct = {
          title: line.replace(/\*\*/g, '').replace(/^\d+\.\s*/, '').trim()
        }
      } else if (line.includes('Brand:')) {
        currentProduct.brand = line.replace('🏷️ Brand:', '').trim()
      } else if (line.includes('Price:')) {
        currentProduct.price = line.replace('💰 Price:', '').trim()
      } else if (line.includes('Buy here:')) {
        currentProduct.url = line.replace('🔗 Buy here:', '').trim()
      }
    }
    
    // Add the last product
    if (currentProduct.title) {
      products.push(currentProduct as ProductRecommendation)
    }
    
    return products
  }

  const isEmergencyResponse = (responseText: string): boolean => {
    const emergencyKeywords = ['emergency', 'urgent', 'immediate', 'EMERGENCY', 'URGENT', 'critical', 'serious']
    return emergencyKeywords.some(keyword => responseText.toLowerCase().includes(keyword.toLowerCase()))
  }

  const renderMessage = (msg: Message, index: number) => {
    const baseMessage = (
      <div key={index} className={`omelo-msg ${msg.sender}`}>
        <div className={`omelo-msg-content ${msg.messageType || 'text'}`}>
          {msg.messageType === 'diagnostic' ? renderDiagnosticMessage(msg) :
           msg.messageType === 'products' ? renderProductsMessage(msg) :
           msg.messageType === 'emergency' ? renderEmergencyMessage(msg) :
           <div dangerouslySetInnerHTML={{ __html: formatMessageText(msg.text) }} />}
        </div>
        <div className="omelo-msg-time">
          {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    )

    return baseMessage
  }

  const renderDiagnosticMessage = (msg: Message) => {
    if (!msg.data || !msg.data.display) {
      return <div>{msg.text}</div>
    }

    const { display } = msg.data
    
    return (
      <div className="diagnostic-container">
        <div className="diagnostic-progress">
          <span className="step-indicator">Step {diagnosticStep} of {maxQuestions}</span>
        </div>
        
        {display.acknowledgment && (
          <div className="acknowledgment">{display.acknowledgment}</div>
        )}
        
        {display.context && (
          <div className="context-info">{display.context}</div>
        )}
        
        {display.question && (
          <div className="question-section">
            <div className="question-text">{display.question.text}</div>
            <div className="options-container">
              {display.question.options?.map((option: any, idx: number) => (
                <button 
                  key={idx}
                  className="diagnostic-option"
                  onClick={() => handleOptionClick(option.copy_text || option.text)}
                >
                  {option.text}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderProductsMessage = (msg: Message) => {
    const products = msg.data || []
    
    return (
      <div className="products-container">
        <div className="products-header">
          <h4>🐾 Recommended Products</h4>
        </div>
        
        <div className="products-grid">
          {products.map((product: ProductRecommendation, idx: number) => (
            <div key={idx} className="product-card">
              <div>
                <div className="product-title">{product.title || 'Product name'}</div>
                <div className="product-price">{product.price || '500'}</div>
              </div>
              <a 
                href={product.url || '#'} 
                target="_blank" 
                rel="noopener noreferrer"
                className="product-link"
              >
                BUY NOW
              </a>
            </div>
          ))}
        </div>
        
        <div className="products-disclaimer ">
          <small>
            <span>These recommendations are based on your pet's needs. Always consult your vet before making changes.</span>
          </small>
        </div>
      </div>
    )
  }

  const renderEmergencyMessage = (msg: Message) => {
    return (
      <div className="emergency-container">
        <div className="emergency-header">
          <span className="emergency-icon">🚨</span>
          <span className="emergency-title">Important Health Information</span>
        </div>
        <div className="emergency-content">
          <div dangerouslySetInnerHTML={{ __html: formatMessageText(msg.text) }} />
        </div>
        <div className="emergency-footer">
          <small>This information does not replace professional veterinary care. Contact your vet if you're concerned about your pet's health.</small>
        </div>
      </div>
    )
  }

  const formatMessageText = (text: string): string => {
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br />')
      .replace(/- (.*)/g, '• $1')
  }

  const handleSend = () => {
    sendMessage(input)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const clearChat = () => {
    setMessages([])
    setDiagnosticStep(0)
    setInDiagnosticFlow(false)
    generateSessionId() // Start fresh session
  }

  // Welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0 && isConnected) {
      const welcomeMessage: Message = {
        sender: 'bot',
        text: 'Hi! I\'m Omelo 👋\n\nAsk me anything — food, symptoms, or behavior. I\'m here 24x7, and you can talk to me in your language too! 🐾',
        timestamp: new Date(),
        messageType: 'text'
      }
      setMessages([welcomeMessage])
    }
  }, [isOpen, isConnected])

  // Expose public methods via ref
  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(!isOpen),
    isOpen: () => isOpen,
    sendMessage: (message: string) => sendMessage(message),
    clearChat: clearChat
  }))

  return (
    <div className={`omelo-widget ${theme} ${position}`}>
      {isOpen && (
        <div className="omelo-chat-box">
          <div className="omelo-header">
            <div className="omelo-title">
              <div className="omelo-logo">
              <img src={omeloLogo} alt="Omelo" className="logo-image" />
              <div className={`connection-dot ${isConnected ? 'connected' : 'disconnected'}`}></div>
              </div>
              <div className="title-content">
                <div className="main-title">Furever Kare</div>
                <div className="powered-by">Powered by Omelo</div>
              </div>
            </div>
            <div className="omelo-controls">
              <button 
                className="omelo-close-btn" 
                onClick={() => setIsOpen(false)}
                title="Close chat"
              >
                <ChevronDown className="chevron-icon" />
              </button>
            </div>
          </div>

          <div className="omelo-messages" ref={messagesContainerRef}>
            {messages.map((msg, index) => renderMessage(msg, index))}
            {isLoading && (
              <div className="omelo-msg bot">
                <div className="omelo-msg-content">
                  <div className="omelo-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="omelo-input">
            {showSuggestions && !isLoading && !inDiagnosticFlow && messages.filter(m => m.sender === 'user').length === 0 && (
              <div className={`suggestion-chips ${suggestionsAnimating ? 'hiding' : ''}`}>
                {suggestionMessages.map((suggestion, index) => (
                  <button
                    key={index}
                    className="suggestion-chip"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
            <div className="input-row">
              <textarea
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyPress}
                placeholder={inDiagnosticFlow ? "Choose above or type your answer..." : "Ask Omelo Anything"}
                disabled={isLoading || !isConnected}
                rows={1}
              />
              <button 
                onClick={handleSend}
                disabled={isLoading || !isConnected || !input.trim()}
                className="omelo-send-btn"
              >
                {isLoading ? '⏳' : <SendHorizontal className="send-icon" />}
              </button>
            </div>
          </div>
        </div>
      )}

      <button 
        className={`omelo-fab ${isOpen ? 'active' : ''}`} 
        onClick={() => setIsOpen(!isOpen)}
        title="Chat with Omelo"
      >
        {isOpen && <span className="close-icon">✖</span>}
      </button>
    </div>
  )
})

export default OmeloWidget

